/**
 * Passkey Management Component
 *
 * Allows users to view, add, and delete their registered passkeys.
 * Provides a comprehensive interface for passkey management.
 */

'use client';

import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Modal, message, Alert, Space, Tag, Popconfirm } from 'antd';
import { KeyOutlined, PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import webauthnService from '@/domains/auth/services/webauthn-service';
import { PasskeyRegistrationForm } from './forms/PasskeyRegistrationForm';

const { Title, Text } = Typography;

interface PasskeyInfo {
  id: string;
  name: string;
  created_at: string;
  last_used_at?: string;
  device_type?: string;
}

interface PasskeyManagementProps {
  userId: string;
  className?: string;
}

export const PasskeyManagement: React.FC<PasskeyManagementProps> = ({
  userId,
  className
}) => {
  const [passkeys, setPasskeys] = useState<PasskeyInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [deletingPasskey, setDeletingPasskey] = useState<string | null>(null);

  // Load user's passkeys
  const loadPasskeys = async () => {
    setLoading(true);
    setError(null);

    try {
      const userPasskeys = await webauthnService.getPasskeys(userId);
      setPasskeys(userPasskeys);
    } catch (err) {
      console.error('Error loading passkeys:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load passkeys';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Delete a passkey
  const handleDeletePasskey = async (passkeyId: string) => {
    setDeletingPasskey(passkeyId);
    setError(null);

    try {
      const success = await webauthnService.deletePasskey(passkeyId);

      if (success) {
        message.success('Passkey deleted successfully');
        // Reload passkeys
        await loadPasskeys();
      } else {
        throw new Error('Failed to delete passkey');
      }
    } catch (err) {
      console.error('Error deleting passkey:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete passkey';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setDeletingPasskey(null);
    }
  };

  // Handle successful passkey registration
  const handleRegistrationSuccess = () => {
    setShowRegistrationModal(false);
    message.success('Passkey registered successfully!');
    // Reload passkeys
    loadPasskeys();
  };

  // Load passkeys on component mount
  useEffect(() => {
    loadPasskeys();
  }, [userId]);

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get device type display name
  const getDeviceTypeDisplay = (deviceType?: string) => {
    switch (deviceType) {
      case 'platform':
        return 'Platform Authenticator';
      case 'cross-platform':
        return 'Security Key';
      default:
        return 'Unknown Device';
    }
  };

  return (
    <Card
      className={className}
      size="small"
      styles={{ header: { minHeight: 44, padding: '10px 16px' }, body: { padding: 16 } }}
      title={
        <Space size={8}>
          <KeyOutlined style={{ color: 'var(--ant-color-primary, #F59E0B)' }} aria-hidden />
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Your passkeys
          </span>
        </Space>
      }
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined aria-hidden />}
          onClick={() => setShowRegistrationModal(true)}
          style={{ cursor: 'pointer' }}
        >
          Add passkey
        </Button>
      }
    >
      {error && (
        <Alert
          title="Couldn’t complete passkey action"
          description={error}
          type="error"
          showIcon
          closable={{ onClose: () => setError(null) }}
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 16px' }} aria-busy="true" aria-live="polite">
          <Text type="secondary">Loading passkeys…</Text>
        </div>
      ) : passkeys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <KeyOutlined
            style={{
              fontSize: 40,
              color: 'var(--ant-color-text-quaternary, #64748b)',
              marginBottom: 12,
            }}
            aria-hidden
          />
          <Title level={4} style={{ marginBottom: 8, fontSize: '1.1rem' }}>
            No passkeys yet
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16, maxWidth: 360, marginInline: 'auto' }}>
            Register a passkey on this device to sign in with biometrics or a security key.
          </Text>
          <Button
            type="primary"
            icon={<PlusOutlined aria-hidden />}
            onClick={() => setShowRegistrationModal(true)}
            style={{ cursor: 'pointer' }}
          >
            Add your first passkey
          </Button>
        </div>
      ) : (
        <div>
            {passkeys.map((passkey) => (
              <div
                key={passkey.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: '1px solid var(--color-border-secondary, #D4C0AE)',
                }}
              >
                <div style={{ display: 'flex', gap: 12, minWidth: 0 }}>
                  <KeyOutlined
                    style={{ fontSize: 22, color: 'var(--ant-color-primary, #F59E0B)', marginTop: 2 }}
                    aria-hidden
                  />
                  <div>
                    <Space wrap size={6}>
                      <Text strong>{passkey.name}</Text>
                      <Tag color="gold">{getDeviceTypeDisplay(passkey.device_type)}</Tag>
                    </Space>
                    <Space orientation="vertical" size={2}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Created: {formatDate(passkey.created_at)}
                      </Text>
                      {passkey.last_used_at && (
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Last used: {formatDate(passkey.last_used_at)}
                        </Text>
                      )}
                    </Space>
                  </div>
                </div>
                <Popconfirm
                  title="Delete passkey"
                  description="This cannot be undone. You may lose passwordless access on this device."
                  onConfirm={() => handleDeletePasskey(passkey.id)}
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  icon={<ExclamationCircleOutlined style={{ color: 'var(--ant-color-error, #EF4444)' }} />}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined aria-hidden />}
                    loading={deletingPasskey === passkey.id}
                    style={{ cursor: 'pointer' }}
                    aria-label={`Delete passkey ${passkey.name}`}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </div>
            ))}
          </div>
      )}

      <Modal
        title="Register new passkey"
        open={showRegistrationModal}
        onCancel={() => setShowRegistrationModal(false)}
        footer={null}
        width={560}
        destroyOnHidden
      >
        <PasskeyRegistrationForm
          onSuccess={handleRegistrationSuccess}
          onError={(err) => {
            setError(err)
            message.error(err)
          }}
        />
      </Modal>
    </Card>
  )
}
